import React from "react";
import { deriveFirebaseImageUrlForWidth } from "@/utils/imageUrlUtils";

interface DerivativesViewerProps {
  baseUrl: string; // a known derivative or original download URL
  widths: number[];
}

export const DerivativesViewer: React.FC<DerivativesViewerProps> = ({ baseUrl, widths }) => {
  const [meta, setMeta] = React.useState<Record<number, { w: number; url: string; width?: number; height?: number; size?: number }>>({});
  const [original, setOriginal] = React.useState<{ label: 'original'; url: string; width?: number; height?: number; size?: number } | null>(null);

  React.useEffect(() => {
    // Guard when baseUrl is not available
    if (!baseUrl) {
      setMeta({});
      return;
    }
    const next: Record<number, { w: number; url: string; width?: number; height?: number; size?: number }> = {};
    for (const w of widths) {
      const url = deriveFirebaseImageUrlForWidth(baseUrl, w, 'webp') || '';
      next[w] = { w, url };
    }
    setMeta(next);
  }, [baseUrl, widths]);

  // Stable key so we only refetch when URLs change (not when dimensions/sizes update)
  const urlKey = React.useMemo(() => widths.map((w) => meta[w]?.url || '').join('|'), [meta, widths]);

  // Load intrinsic dimensions via Image object and fetch size via HEAD (best-effort)
  React.useEffect(() => {
    if (!baseUrl) return; // nothing to fetch without a base URL
    widths.forEach((w) => {
      const entry = meta[w];
      if (!entry?.url) return;
      // Dimensions
      const img = new Image();
      img.onload = () => {
        setMeta((prev) => ({ ...prev, [w]: { ...prev[w], width: img.naturalWidth, height: img.naturalHeight } }));
      };
      img.src = entry.url;
      // Size
      fetch(entry.url, { method: 'HEAD' })
        .then((res) => {
          const len = res.headers.get('content-length');
          if (len) {
            setMeta((prev) => ({ ...prev, [w]: { ...prev[w], size: Number(len) } }));
          }
        })
        .catch(() => {
          // Intentionally ignore HEAD request failures
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey]);

  // Resolve original image URL by probing likely candidates (strip __w and try jpg/png/webp)
  React.useEffect(() => {
    if (!baseUrl) {
      setOriginal(null);
      return;
    }
    try {
      const url = new URL(baseUrl);
      if (!/firebasestorage\.googleapis\.com$/.test(url.hostname)) {
        setOriginal({ label: 'original', url: baseUrl });
        return;
      }
      const segments = url.pathname.split('/').filter(Boolean);
      const bIndex = segments.indexOf('b');
      const oIndex = segments.indexOf('o');
      if (bIndex === -1 || oIndex === -1 || oIndex + 1 >= segments.length) {
        setOriginal({ label: 'original', url: baseUrl });
        return;
      }
      const bucket = segments[bIndex + 1];
      const encodedPath = segments[oIndex + 1];
      const token = url.searchParams.get('token') || '';
      const objectPath = decodeURIComponent(encodedPath);
      const lastSlash = objectPath.lastIndexOf('/');
      const dir = objectPath.substring(0, lastSlash);
      const file = objectPath.substring(lastSlash + 1);
      const dot = file.lastIndexOf('.');
      const ext = dot === -1 ? '' : file.substring(dot + 1).toLowerCase();
      const base = dot === -1 ? file : file.substring(0, dot);
      const baseWithoutWidth = base.replace(/__w\d+$/, '');
      const tryExts = ext === 'webp' ? ['jpg', 'png', 'webp'] : [ext || 'jpg', 'jpg', 'png', 'webp'];

      (async () => {
        for (const e of tryExts) {
          const p = `${dir}/${baseWithoutWidth}.${e}`;
          const candidate = new URL(`https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(p)}`);
          candidate.searchParams.set('alt', 'media');
          if (token) candidate.searchParams.set('token', token);
          try {
            const head = await fetch(candidate.toString(), { method: 'HEAD' });
            if (head.ok) {
              const len = head.headers.get('content-length');
              const urlStr = candidate.toString();
              const img = new Image();
              img.onload = () => setOriginal((prev) => ({ label: 'original', url: urlStr, width: img.naturalWidth, height: img.naturalHeight, size: len ? Number(len) : prev?.size }));
              img.src = urlStr;
              setOriginal({ label: 'original', url: urlStr, size: len ? Number(len) : undefined });
              return;
            }
          } catch (_err) {
            // Ignore candidate fetch errors
          }
        }
        // Fallback to base URL if none resolved
        setOriginal({ label: 'original', url: baseUrl });
      })();
    } catch {
      setOriginal({ label: 'original', url: baseUrl });
    }
  }, [baseUrl]);

  if (!baseUrl) return null;

  return (
    <div className="mt-4 border-t pt-3">
      <div className="text-sm font-medium mb-2">Image derivatives</div>
      <div className="space-y-2">
        {widths.map((w) => {
          const r = meta[w];
          return (
            <div key={w} className="flex items-center gap-3">
              {r?.url && (
                <img src={r.url} width={80} height={Math.round(80 * 0.66)} alt={`${w}w`} className="rounded border" />
              )}
              <div className="text-xs break-all">
                <div className="flex items-center gap-2">
                  <span className="font-mono">{w}w</span>
                  {r?.width && r?.height ? (
                    <span className="text-muted-foreground">{r.width}×{r.height}px</span>
                  ) : null}
                  {typeof r?.size === 'number' ? (
                    <span className="text-muted-foreground">{formatBytes(r.size)}</span>
                  ) : null}
                </div>
                <div className="text-muted-foreground">{r?.url || 'n/a'}</div>
              </div>
            </div>
          );
        })}
        {original && (
          <div className="flex items-center gap-3">
            {original.url && (
              <img src={original.url} width={80} height={Math.round(80 * 0.66)} alt={`original`} className="rounded border" />
            )}
            <div className="text-xs break-all">
              <div className="flex items-center gap-2">
                <span className="font-mono">original</span>
                {original.width && original.height ? (
                  <span className="text-muted-foreground">{original.width}×{original.height}px</span>
                ) : null}
                {typeof original.size === 'number' ? (
                  <span className="text-muted-foreground">{formatBytes(original.size)}</span>
                ) : null}
              </div>
              <div className="text-muted-foreground">{original.url}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(2)} MB`;
}


